import React, { useCallback, useState } from 'react';

import _get from 'lodash/get';

import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import alert from 'react-hot-toast';

import { withContext } from './NotificationContext';
import i18n from './messages.json';
import dateUtil from '../../utils/date-util';

import './Notification.css';

/**
 * "Scheduled Pushes" section on /notifications (WWW-15236).
 *
 * Lists still-pending scheduled pushes (context.scheduledMessages) with the
 * Notification, Sender, Scheduled delivery time, and Edit / Cancel actions. Edit
 * reopens the push in the compose modal (reschedule/edit); Cancel deletes the
 * Braze schedule + the record after a confirm. Once a push's time passes it drops
 * out of this list and reads as delivered in the Activity Log.
 */
const ScheduledPushesList = ({ context, openEditNotification }) => {
  const { scheduledMessages = [], cancelScheduledMessage } = context;
  const labels = i18n.ink.notification.scheduled;
  const header = i18n.ink.notification.listTable.table.header;

  const [pendingCancel, setPendingCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const confirmCancel = useCallback(async () => {
    if (!pendingCancel) return;

    setIsCancelling(true);

    try {
      await cancelScheduledMessage(pendingCancel);
      alert.success(labels.cancelSuccess);
      setPendingCancel(null);
    } catch (e) {
      console.error('Failed to cancel scheduled push', e);
      alert.error(labels.cancelFailure);
    } finally {
      setIsCancelling(false);
    }
  }, [pendingCancel, cancelScheduledMessage, labels]);

  return (
    <div className='scheduled-pushes'>
      <Container className='Notif-list'>
        <Row className='header'>
          <Col>{labels.sectionTitle}</Col>
        </Row>
      </Container>

      <div className='notif-table-card'>
        <Table responsive className='Notif-list'>
          <thead>
            <tr>
              <th>{header.title}</th>
              <th>{header.sender}</th>
              <th>{header.expectedDelivery}</th>
              <th aria-label='actions' />
            </tr>
          </thead>
          <tbody>
            {scheduledMessages.length === 0 ? (
              <tr>
                <td colSpan='4' className='no-items'>{labels.empty}</td>
              </tr>
            ) : (
              scheduledMessages.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className='scheduled-pushes__notif'>
                      <div className='scheduled-pushes__notif-title'>{item.title}</div>
                      {item.body && <div className='scheduled-pushes__notif-body'>{item.body}</div>}
                    </div>
                  </td>
                  <td>{_get(item, 'user.name', '—')}</td>
                  <td className='notif-time-cell'>
                    <Badge variant='warning'>{labels.badgeScheduled}</Badge>{' '}
                    {dateUtil.formatEtDateTime(item.scheduledSendTime)}
                  </td>
                  <td className='scheduled-pushes__actions'>
                    <Button
                      variant='outline-secondary'
                      size='sm'
                      onClick={() => openEditNotification(item.id)}
                    >
                      {i18n.ink.notification.buttons.edit}
                    </Button>
                    <Button
                      variant='outline-danger'
                      size='sm'
                      onClick={() => setPendingCancel(item)}
                    >
                      {i18n.ink.notification.buttons.cancelSchedule}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal
        show={Boolean(pendingCancel)}
        onHide={() => (isCancelling ? null : setPendingCancel(null))}
        centered
        dialogClassName='scheduled-cancel-dialog'
      >
        <Modal.Header closeButton>
          <Modal.Title>{labels.confirmCancelTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{labels.confirmCancelBody}</Modal.Body>
        <Modal.Footer>
          <Button
            variant='light'
            onClick={() => setPendingCancel(null)}
            disabled={isCancelling}
            style={{ marginRight: 'auto' }}
          >
            {labels.keepScheduled}
          </Button>
          <Button variant='danger' onClick={confirmCancel} disabled={isCancelling}>
            {labels.confirmCancelButton}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default withContext(ScheduledPushesList);
